import { useId } from 'react';
import styled from 'styled-components';
import { combineLatest, map } from 'rxjs';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { BUTTON_CONTENT_SELECTOR, Button, ButtonType } from '@ardoq/button';
import { AiStarIcon } from '@ardoq/ai';
import { t, type I18nLocale } from '@ardoq/i18n';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { showViewSidePanel } from 'views/mainApp/viewSidePanel/actions';
import { ViewSidePanelContentIds } from 'views/mainApp/viewSidePanel/types';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { aiDraftChanges$, type ViewChangingCommandId } from './aiDraftChanges$';

type AiDraftMarkerProps =
  | { isVisible: false }
  | { isVisible: true; label: string; tooltip: string };

const openSaveAsViewpoint = () =>
  dispatchAction(showViewSidePanel(ViewSidePanelContentIds.SAVE_VIEWPOINT));

const AiDraftMarker = (props: AiDraftMarkerProps) => {
  const descriptionId = useId();
  if (!props.isVisible) {
    return null;
  }
  return (
    <>
      <ShrinkableButton
        buttonType={ButtonType.GHOST}
        onClick={openSaveAsViewpoint}
        data-tooltip-text={props.tooltip}
        aria-describedby={descriptionId}
      >
        <AiStarIcon aiColored />
        {props.label}
      </ShrinkableButton>
      <VisuallyHidden id={descriptionId}>{props.tooltip}</VisuallyHidden>
    </>
  );
};

const ShrinkableButton = styled(Button)`
  min-width: 0;

  ${BUTTON_CONTENT_SELECTOR} {
    min-width: 0;
    overflow: hidden;
  }
`;

const toChangeLabel = (
  i18nLocale: I18nLocale,
  commandId: ViewChangingCommandId
): string => {
  switch (commandId) {
    case AiControlledCommandId.APPLY_CONDITIONAL_FORMATTING:
      return t(
        i18nLocale,
        'appLayout:conditionalFormatRowLabel',
        'Conditional format'
      );
    case AiControlledCommandId.APPLY_LABEL_FORMATTING:
      return t(i18nLocale, 'appLayout:labelsLabel', 'Labels');
    case AiControlledCommandId.CHANGE_VIEW_SETTINGS:
      return t(
        i18nLocale,
        'appLayout:aiDraftMarkerViewSettings',
        'View settings'
      );
    case AiControlledCommandId.CHANGE_TRAVERSAL_CONTEXT:
      return t(i18nLocale, 'appLayout:aiDraftMarkerContext', 'Context');
    case AiControlledCommandId.LAYOUT_BLOCKS:
      return t(i18nLocale, 'appLayout:viewPaneHeader.layout', 'Layout');
    default:
      return exhaustiveCheck(commandId);
  }
};

const toAiDraftMarkerProps = (
  changes: ViewChangingCommandId[],
  i18nLocale: I18nLocale
): AiDraftMarkerProps => {
  if (changes.length === 0) {
    return { isVisible: false };
  }
  const count = changes.length;
  const areas = [
    ...new Set(changes.map(change => toChangeLabel(i18nLocale, change))),
  ].join(', ');
  return {
    isVisible: true,
    label: t(
      i18nLocale,
      count === 1
        ? 'appLayout:aiDraftMarkerLabel_one'
        : 'appLayout:aiDraftMarkerLabel_other',
      { count },
      `Draft · {{count}} AI ${count === 1 ? 'change' : 'changes'}`
    ),
    tooltip: t(
      i18nLocale,
      'appLayout:aiDraftMarkerTooltip',
      { areas },
      'AI changed: {{areas}}. Not saved yet. Click to save as a viewpoint.'
    ),
  };
};

const aiDraftMarkerProps$ = combineLatest([aiDraftChanges$, i18nLocale$]).pipe(
  map(([{ changes }, i18nLocale]) => toAiDraftMarkerProps(changes, i18nLocale))
);

export const ConnectedAiDraftMarker = connect(
  AiDraftMarker,
  aiDraftMarkerProps$
);
